export const PROCUREMENT_IMPORT_COLUMNS = [
  "Spec Section - Spec Section Name",
  "Submittal Number",
  "Title",
  "Revision",
  "Type",
  "Responsible Contractor",
  "Contract Issued Release",
  "Submittal Prep Time",
  "Submittal Issued",
  "Approval Time",
  "Submittal Returned",
  "Lead Time",
  "Schedule ID",
  "Date Required On Site",
  "Sub Committed Delivery Date",
  "Actual Delivery Date",
];

const PROCUREMENT_IMPORT_FIELD_ALIASES = {
  specSection: ["Spec Section - Spec Section Name"],
  specNumber: ["Submittal Number"],
  specTitle: ["Title"],
  specRevision: ["Revision"],
  specType: ["Type"],
  responsibleContractor: ["Responsible Contractor"],
  contractIssuedRelease: ["Contract Issued Release"],
  submittalPrepTime: ["Submittal Prep Time"],
  submittalIssued: ["Submittal Issued"],
  approvalTime: ["Approval Time"],
  submittalReturned: [
    "Submittal Returned",
    "Submittal Re     turned",
    "Submittal Returne",
  ],
  leadTime: ["Lead Time"],
  scheduleID: ["Schedule ID"],
  dateRequiredOnSite: ["Date Required On Site"],
  subCommittedDeliveryDate: ["Sub Committed Delivery Date"],
  actualDeliveryDate: ["Actual Delivery Date"],
};

const PROCUREMENT_IMPORT_DATE_FIELDS = new Set([
  "contractIssuedRelease",
  "submittalIssued",
  "submittalReturned",
  "dateRequiredOnSite",
  "subCommittedDeliveryDate",
  "actualDeliveryDate",
]);

function normalizeImportColumnKey(value) {
  return String(value || "")
    .replace(/\s+/g, " ")
    .trim()
    .toLowerCase();
}

function findValueByAliases(row, aliases = []) {
  const rowEntries = Object.entries(row || {});

  for (const alias of aliases) {
    const normalizedAlias = normalizeImportColumnKey(alias);
    const matchedEntry = rowEntries.find(
      ([key]) => normalizeImportColumnKey(key) === normalizedAlias,
    );

    if (matchedEntry) {
      return matchedEntry[1] ?? "";
    }
  }

  return "";
}

function padDatePart(value) {
  return String(value).padStart(2, "0");
}

function formatDateParts(month, day, year) {
  return `${padDatePart(month)}-${padDatePart(day)}-${year}`;
}

function formatDateInstance(dateValue, useUtc = false) {
  if (!(dateValue instanceof Date) || Number.isNaN(dateValue.getTime())) {
    return "";
  }

  const month = useUtc
    ? dateValue.getUTCMonth() + 1
    : dateValue.getMonth() + 1;
  const day = useUtc ? dateValue.getUTCDate() : dateValue.getDate();
  const year = useUtc ? dateValue.getUTCFullYear() : dateValue.getFullYear();

  return formatDateParts(month, day, year);
}

function convertExcelSerialDateToString(value) {
  if (!Number.isFinite(value)) {
    return "";
  }

  const excelEpochUtc = Date.UTC(1899, 11, 30);
  const millisecondsPerDay = 24 * 60 * 60 * 1000;
  const serialDays = Math.floor(value);
  const serialTimeMs = Math.round((value - serialDays) * millisecondsPerDay);
  const normalizedDate = new Date(
    excelEpochUtc + serialDays * millisecondsPerDay + serialTimeMs,
  );

  return formatDateInstance(normalizedDate, true);
}

export function normalizeProcurementDateValue(value) {
  if (value === null || value === undefined) {
    return "";
  }

  if (value instanceof Date) {
    return formatDateInstance(value);
  }

  if (typeof value === "number") {
    return convertExcelSerialDateToString(value);
  }

  const normalizedValue = String(value).trim();

  if (!normalizedValue) {
    return "";
  }

  const mmDdYyyyMatch = normalizedValue.match(/^(\d{2})-(\d{2})-(\d{4})$/);
  if (mmDdYyyyMatch) {
    const [, month, day, year] = mmDdYyyyMatch;
    return formatDateParts(month, day, year);
  }

  return normalizedValue;
}

function findNormalizedValueByAliases(row, fieldKey) {
  const directFieldValue = row?.[fieldKey];
  const hasDirectFieldValue =
    directFieldValue !== undefined && directFieldValue !== null;

  if (hasDirectFieldValue) {
    if (PROCUREMENT_IMPORT_DATE_FIELDS.has(fieldKey)) {
      return normalizeProcurementDateValue(directFieldValue);
    }

    return directFieldValue;
  }

  const rawValue = findValueByAliases(
    row,
    PROCUREMENT_IMPORT_FIELD_ALIASES[fieldKey] || [],
  );

  if (PROCUREMENT_IMPORT_DATE_FIELDS.has(fieldKey)) {
    return normalizeProcurementDateValue(rawValue);
  }

  return rawValue;
}

function normalizeErrorMessages(row = {}) {
  const rawErrors =
    row.errorMessages ?? row.errorMessage ?? row.errors ?? row.issueMessages;

  if (Array.isArray(rawErrors)) {
    return rawErrors
      .flatMap((errorValue) =>
        Array.isArray(errorValue) ? errorValue : [errorValue],
      )
      .map((errorValue) => String(errorValue || "").trim())
      .filter(Boolean);
  }

  if (rawErrors && typeof rawErrors === "object") {
    return Object.values(rawErrors)
      .map((errorValue) => String(errorValue || "").trim())
      .filter(Boolean);
  }

  if (rawErrors) {
    return [String(rawErrors).trim()].filter(Boolean);
  }

  return [];
}

export function normalizeProcurementImportRow(row = {}) {
  return {
    ...row,
    lineCnt: row.lineCnt ?? row["Line #"] ?? row.lineNumber ?? "",
    specSection: findNormalizedValueByAliases(row, "specSection"),
    specNumber: findNormalizedValueByAliases(row, "specNumber"),
    specTitle: findNormalizedValueByAliases(row, "specTitle"),
    specRevision: findNormalizedValueByAliases(row, "specRevision"),
    specType: findNormalizedValueByAliases(row, "specType"),
    responsibleContractor: findNormalizedValueByAliases(
      row,
      "responsibleContractor",
    ),
    contractIssuedRelease: findNormalizedValueByAliases(
      row,
      "contractIssuedRelease",
    ),
    submittalPrepTime: findNormalizedValueByAliases(row, "submittalPrepTime"),
    submittalIssued: findNormalizedValueByAliases(row, "submittalIssued"),
    approvalTime: findNormalizedValueByAliases(row, "approvalTime"),
    submittalReturned: findNormalizedValueByAliases(row, "submittalReturned"),
    leadTime: findNormalizedValueByAliases(row, "leadTime"),
    scheduleID: findNormalizedValueByAliases(row, "scheduleID"),
    dateRequiredOnSite: findNormalizedValueByAliases(
      row,
      "dateRequiredOnSite",
    ),
    subCommittedDeliveryDate: findNormalizedValueByAliases(
      row,
      "subCommittedDeliveryDate",
    ),
    actualDeliveryDate: findNormalizedValueByAliases(
      row,
      "actualDeliveryDate",
    ),
    errorMessages: normalizeErrorMessages(row),
  };
}

export function normalizeProcurementImportRows(rows) {
  return Array.isArray(rows) ? rows.map(normalizeProcurementImportRow) : [];
}

export function mapProcurementImportRowForApi(row = {}) {
  return {
    "Spec Section - Spec Section Name": row.specSection || "",
    "Submittal Number": row.specNumber || "",
    Title: row.specTitle || "",
    Revision: row.specRevision || "",
    Type: row.specType || "",
    Response: row.specResponse || "",
    "Responsible Contractor": row.responsibleContractor || "",
    "Issue Contract": row.issueContract || "",
    "Contract Issued Release": row.contractIssuedRelease || "",
    "Submittal Prep Time": row.submittalPrepTime || "",
    "Submittal Required": row.submittalRequired || "",
    "Submittal Issued": row.submittalIssued || "",
    "Approval Time": row.approvalTime || "",
    "Submittal Approval Due": row.submittalApprovalDue || "",
    "Submittal Returned": row.submittalReturned || "",
    "Lead Time": row.leadTime || "",
    "Schedule ID": row.scheduleID || "",
    "Date Required On Site": row.dateRequiredOnSite || "",
    "Projected Delivery Date": row.projectedDeliveryDate || "",
    "Sub Committed Delivery Date": row.subCommittedDeliveryDate || "",
    "Actual Delivery Date": row.actualDeliveryDate || "",
  };
}

export function validateProcurementImportHeaders(headerRow = []) {
  const sanitizedHeaderRow = (Array.isArray(headerRow) ? headerRow : []).filter(
    (columnName) => String(columnName || "").trim() !== "",
  );
  const normalizedExpectedColumns = new Map(
    PROCUREMENT_IMPORT_COLUMNS.map((columnName) => [
      normalizeImportColumnKey(columnName),
      columnName,
    ]),
  );
  const normalizedActualHeaders = sanitizedHeaderRow.map((columnName) =>
    normalizeImportColumnKey(columnName),
  );

  const missingColumns = PROCUREMENT_IMPORT_COLUMNS.filter(
    (columnName) =>
      !normalizedActualHeaders.includes(normalizeImportColumnKey(columnName)),
  );
  const unexpectedColumns = sanitizedHeaderRow.filter(
    (columnName) =>
      !normalizedExpectedColumns.has(normalizeImportColumnKey(columnName)),
  );

  return {
    isValid: missingColumns.length === 0 && unexpectedColumns.length === 0,
    missingColumns,
    unexpectedColumns,
  };
}
